export type GuardianUiRunStatus =
  | 'queued'
  | 'running'
  | 'paused'
  | 'human_owned'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'superseded'
  | 'terminated'
  | string;

export type GuardianUiControlState = 'none' | 'pause_requested' | 'termination_requested' | 'human_takeover_requested' | string;
export type GuardianUiOwner = 'none' | 'neo' | 'guardian' | 'human' | string;

export interface GuardianControlAvailabilityInput {
  runStatus?: GuardianUiRunStatus | null;
  controlState?: GuardianUiControlState | null;
  owner?: GuardianUiOwner | null;
  eligibilityStatus?: string | null;
  incidentStatus?: string | null;
  lifecycleBypassEnabled?: boolean;
  recommendationDisposition?: string | null;
  authoritativeNeoReady?: boolean;
}

export interface GuardianControlAvailability {
  pause: boolean;
  pauseRequested: boolean;
  resume: boolean;
  takeOwnership: boolean;
  returnToGuardian: boolean;
  terminate: boolean;
  reevaluate: boolean;
  start: boolean;
}

const TERMINAL = new Set(['completed', 'cancelled', 'terminated']);

export function guardianControlAvailability(input: GuardianControlAvailabilityInput): GuardianControlAvailability {
  const runStatus = String(input.runStatus ?? '').toLowerCase();
  const controlState = String(input.controlState ?? 'none').toLowerCase();
  const owner = String(input.owner ?? 'none').toLowerCase();
  const hasRun = Boolean(runStatus);
  const terminal = TERMINAL.has(runStatus);
  const noControlRequest = !controlState || controlState === 'none';
  const lifecycle = String(input.incidentStatus ?? '').trim().toLowerCase();
  const lifecycleAllowed = lifecycle === 'new' || lifecycle === 'active' || Boolean(input.lifecycleBypassEnabled);
  const noActiveRun = !hasRun || terminal || ['failed','superseded'].includes(runStatus);

  return {
    pause: owner === 'guardian' && runStatus === 'running' && noControlRequest,
    pauseRequested: runStatus === 'running' && controlState === 'pause_requested',
    resume: owner === 'guardian' && runStatus === 'paused' && noControlRequest,
    takeOwnership: owner === 'guardian' && noControlRequest && (runStatus === 'running' || runStatus === 'paused' || (runStatus === 'completed' && String(input.recommendationDisposition ?? '').toUpperCase() === 'HUMAN_ESCALATION')),
    returnToGuardian: owner === 'human' && noControlRequest && (runStatus === 'human_owned' || Boolean(input.authoritativeNeoReady)),
    terminate: hasRun && !terminal && runStatus !== 'queued' ? true : hasRun && runStatus === 'queued',
    reevaluate: true,
    start: owner === 'none' && Boolean(input.authoritativeNeoReady) && String(input.eligibilityStatus ?? '').toLowerCase() === 'eligible' && noActiveRun && lifecycleAllowed,
  };
}

export function guardianOperationalLabel(runStatus?: string | null, controlState?: string | null): string {
  const status = String(runStatus ?? '').toLowerCase();
  const control = String(controlState ?? 'none').toLowerCase();
  if (control === 'pause_requested') return 'Pause requested';
  if (control === 'termination_requested') return 'Termination requested';
  if (control === 'human_takeover_requested') return 'Human takeover requested';
  if (!status) return 'Not started';
  if (status === 'neo_skipped') return 'Latest Neo attempt skipped';
  const words = status.split('_').filter(Boolean);
  if (!words.length) return 'Not started';
  const first = words[0]!;
  return [first[0]!.toUpperCase() + first.slice(1), ...words.slice(1)].join(' ');
}


function displayLifecycleStatus(status?: string | null): string | null {
  const raw = String(status ?? '').trim();
  if (!raw) return null;
  const normalized = raw.toLowerCase();
  if (normalized === 'new') return 'New';
  if (normalized === 'active' || normalized === 'in progress' || normalized === 'inprogress') return 'Active';
  if (normalized === 'closed' || normalized === 'resolved') return 'Closed';
  if (normalized === 'redirected') return 'Redirected';
  return raw;
}

export function guardianExecutionEligibilityLabel(policyEligibility?: string | null, incidentStatus?: string | null, lifecycleBypassEnabled = false): string {
  if (String(policyEligibility ?? '').toLowerCase() !== 'eligible') return 'Policy ineligible';
  const status = displayLifecycleStatus(incidentStatus);
  if (!status) return lifecycleBypassEnabled ? 'Policy eligible · Lifecycle bypass active (Unknown)' : 'Policy eligible · Lifecycle unknown';
  if (status === 'New' || status === 'Active') return 'Ready to start';
  if (lifecycleBypassEnabled) return `Policy eligible · Lifecycle bypass active (${status})`;
  return `Policy eligible · Lifecycle blocked (${status})`;
}


export function guardianExecutionStateLabel(runStatus?: string | null, phase?: string | null, disposition?: string | null, controlState?: string | null): string {
  const status=String(runStatus ?? '').toLowerCase();
  const control=String(controlState ?? 'none').toLowerCase();
  const d=String(disposition ?? '').toUpperCase();
  if (control==='pause_requested') return 'Pause requested';
  if (control==='termination_requested') return 'Termination requested';
  if (control==='human_takeover_requested') return 'Human takeover requested';
  if (!status) return 'Not started';
  if (status==='completed') {
    if (d==='HUMAN_ESCALATION') return 'Completed · Human escalation';
    if (d==='RECOMMEND_RESPONSE') return 'Completed · Response recommended';
    if (d==='TEST_RECOMMENDATION') return 'Completed · Test recommendation';
    if (d==='TEST_REJECTED') return 'Completed · Test rejected';
    if (d==='POLICY_BLOCKED') return 'Completed · Policy blocked';
    if (d==='NO_ACTION_RECOMMENDED') return 'Completed · No action recommended';
    return 'Completed';
  }
  if (status==='running') return `Running · ${String(phase ?? 'Guardian').replaceAll('_',' ')}`;
  if (status==='paused') return 'Paused · Safe checkpoint';
  if (status==='human_owned') return 'Human owned';
  if (status==='failed') return 'Failed';
  if (status==='terminated') return 'Terminated by analyst';
  if (status==='superseded') return String(phase ?? '').toLowerCase()==='neo_revision_superseded' ? 'Superseded · Newer Neo revision' : 'Superseded · Ownership lost';
  return guardianOperationalLabel(runStatus,controlState);
}

export function guardianDecisionColumns(recommendation?: any): { commander:string; judge:string; policy:string } {
  const primary=recommendation?.primaryAction ?? recommendation?.candidates?.[0] ?? null;
  if (!primary) {
    if (['NO_RESPONSE_REQUIRED','GUARDIAN_ADJUDICATION_ESCALATE','INSUFFICIENT_EVIDENCE'].includes(String(recommendation?.reasonCode ?? ''))) return {commander:'Not required',judge:'Not required',policy:'Not required'};
    if (String(recommendation?.reasonCode ?? '') === 'EVIDENCE_LIMITED_NO_SAFE_RECOMMENDATION') return {commander:'No safe proposal',judge:'Not run',policy:'Not run'};
    return {commander:'No proposal',judge:'-',policy:'-'};
  }
  const words=(v:unknown)=>String(v ?? '').replaceAll('_',' ');
  const commanderLabel = `${primary.label ?? primary.actionId ?? 'Proposal'}${primary.confidence ? ` · ${primary.confidence}` : ''}`;
  return {
    commander:primary.testSynthetic ? `No safe proposal → Test fallback: ${commanderLabel}` : commanderLabel,
    judge:primary.judgeVerdict ? `${words(primary.judgeVerdict)}${primary.judgeConfidence ? ` · ${primary.judgeConfidence}` : ''}` : '-',
    policy:primary.policyDecision ? words(primary.policyDecision) : '-',
  };
}

export function guardianAutonomyStateLabel(result:any): string {
  const disposition=String(result?.disposition ?? '').toUpperCase();
  if (!disposition) return 'Not executed';
  if (disposition==='AUTONOMOUS_RESPONSE_VERIFIED') return 'Verified autonomous response';
  if (disposition==='AUTONOMOUS_RESPONSE_ACCEPTED_PENDING') return 'Autonomous response accepted · verification pending';
  if (disposition==='AUTONOMOUS_RESPONSE_PARTIAL') return 'Autonomous response partially verified';
  if (disposition==='AUTONOMOUS_RESPONSE_FAILED') return 'Autonomous response failed';
  if (disposition==='AUTONOMY_BLOCKED') return 'Autonomy blocked';
  if (disposition==='AUTONOMY_SKIPPED') return 'Autonomy skipped';
  return disposition.replaceAll('_',' ');
}

export function guardianAutonomyColumns(result:any): {autonomy:string;action:string;provider:string;execution:string;verification:string} {
  const first=result?.actions?.[0] ?? null;
  const disposition=String(result?.disposition ?? '').toUpperCase();
  const autonomy=disposition==='AUTONOMOUS_RESPONSE_VERIFIED' ? 'VERIFIED'
    : disposition==='AUTONOMOUS_RESPONSE_ACCEPTED_PENDING' ? 'PENDING'
    : disposition==='AUTONOMOUS_RESPONSE_PARTIAL' ? 'PARTIAL'
    : disposition==='AUTONOMOUS_RESPONSE_FAILED' ? 'FAILED'
    : disposition==='AUTONOMY_BLOCKED' ? 'BLOCKED'
    : disposition==='AUTONOMY_SKIPPED' ? 'SKIPPED' : '-';
  return {
    autonomy,
    action:String(first?.actionId ?? '-'),
    provider:String(first?.provider ?? '-'),
    execution:String(first?.executionStatus ?? '-'),
    verification:String(first?.verificationState ?? '-'),
  };
}

export function guardianAdjudicationColumns(adjudication:any): {relationship:string;verdict:string;response:string} {
  if (!adjudication) return {relationship:'-',verdict:'-',response:'-'};
  const words=(v:unknown)=>String(v ?? '').replaceAll('_',' ');
  const relationship=words(adjudication.relationship || '-');
  const verdict=`${words(adjudication.verdict || '-')}${adjudication.confidence == null ? '' : ` · ${adjudication.confidence}%`}`;
  const response=adjudication.responseRequired === true ? 'Required' : String(adjudication.relationship ?? '').toUpperCase()==='INCONCLUSIVE' ? 'Not required · evidence limited' : 'Not required';
  return {relationship,verdict,response};
}

export function guardianSentinelSummaryLabel(status?:string|null): string {
  const v=String(status ?? 'not_attempted').toLowerCase();
  if (v==='posted') return 'Posted to Sentinel';
  if (v==='posting') return 'Posting to Sentinel';
  if (v==='failed') return 'Sentinel summary failed';
  if (v==='disabled') return 'Disabled by policy';
  if (v==='skipped_unbound_neo') return 'Skipped · Neo FINAL not bound';
  if (v==='skipped_superseded') return 'Skipped · newer Neo revision';
  return 'Not posted';
}

export function guardianAuthoritativeNeoLabel(ready:boolean,revision?:number|null,evidenceSnapshotId?:string|null):string {
  if (!ready || revision==null || !evidenceSnapshotId) return 'Neo FINAL + evidence required';
  return `Neo FINAL r${revision} · evidence ready`;
}

export function guardianAdjudicationContractLabel(adjudication:any):string {
  const source=String(adjudication?.contractSource ?? '');
  const model=String(adjudication?.contractModel ?? '').trim();
  if (source==='fail_closed_fallback') return 'Fail-closed fallback';
  if (source==='structured_finalizer_repaired') return `Structured · repaired${model?` · ${model}`:''}`;
  if (source==='structured_finalizer') return `Structured${model?` · ${model}`:''}`;
  return 'Legacy / unknown contract';
}
